/**
 * BillsPage — Recurring bills and subscriptions management
 *
 * Sub-modes: list | add | edit
 *
 * Features:
 *   - List all bills sorted by due date, grouped by paid/unpaid
 *   - Toggle paid/unpaid for current billing cycle
 *   - Add new bills manually
 *   - Edit existing bills (name, amount, due day, category, bucket)
 *   - Delete bills
 *   - Filter: all | unpaid | subscriptions
 *   - Shows total upcoming and total monthly
 *
 * Phase 2: Manual bill entry and paid toggle
 * Phase 6: Auto-detection from Monarch, enhanced subscription tracking
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getBills, addBill, updateBill, deleteBill, toggleBillPaid,
  getSubscriptionSuggestions, acceptSuggestion, dismissSuggestion
} from '../services/db';
import { formatCurrency, ordinalDay } from '../utils/format';
import { getBillUrgency } from '../utils/dates';
import { SkeletonLine, SkeletonCard } from '../components/Skeleton';
import theme from '../utils/theme';

// ==================== HELPERS ====================

/**
 * Get current month key (YYYY-MM).
 */
function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Check if a subscription bill is stale (no matched payment in 2+ months).
 * Only applies to active subscriptions.
 *
 * @param {Object} bill - Bill object
 * @returns {{ stale: boolean, lastMonth: string|null }}
 */
function getStaleStatus(bill) {
  if (!bill.is_subscription || !bill.active) return { stale: false, lastMonth: null };

  const current = getCurrentMonthKey();
  const [cy, cm] = current.split('-').map(Number);

  if (bill.last_matched_month) {
    const [ly, lm] = bill.last_matched_month.split('-').map(Number);
    const monthsDiff = (cy - ly) * 12 + (cm - lm);
    return { stale: monthsDiff >= 2, lastMonth: bill.last_matched_month };
  }

  // Fallback: check created_at timestamp
  if (bill.created_at) {
    const created = bill.created_at.toDate ? bill.created_at.toDate() : new Date(bill.created_at);
    const createdKey = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
    const [ccy, ccm] = createdKey.split('-').map(Number);
    const monthsDiff = (cy - ccy) * 12 + (cm - ccm);
    return { stale: monthsDiff >= 2, lastMonth: null };
  }

  return { stale: false, lastMonth: null };
}

export default function BillsPage() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | add | edit
  const [filter, setFilter] = useState('all'); // all | unpaid | subscriptions
  const [editingBill, setEditingBill] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '', amount: '', due_day: '',
    bucket: 'need', frequency: 'monthly', auto_pay: false,
    is_subscription: false, variable: false, match_pattern: '',
  });

  /**
   * Load all bills from Supabase.
   */
  const loadBills = useCallback(async () => {
    try {
      const [data, sug] = await Promise.all([
        getBills(user.id),
        getSubscriptionSuggestions(user.id),
      ]);
      setBills(data);
      setSuggestions(sug);
    } catch (err) {
      console.error('Failed to load bills:', err);
      setError(err.message || 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadBills(); }, [loadBills]);

  /**
   * Filter bills based on current filter selection.
   */
  const filteredBills = bills.filter(bill => {
    if (!bill.active) return false;
    if (filter === 'unpaid') return !bill.current_cycle_paid;
    if (filter === 'subscriptions') return bill.is_subscription;
    return true;
  });

  // Sort: unpaid first, then by due_day
  const sortedBills = [...filteredBills].sort((a, b) => {
    if (a.current_cycle_paid !== b.current_cycle_paid) {
      return a.current_cycle_paid ? 1 : -1;
    }
    return (a.due_day || 0) - (b.due_day || 0);
  });

  // Summary calculations
  const unpaidTotal = bills.filter(b => b.active && !b.current_cycle_paid)
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const monthlyTotal = bills.filter(b => b.active)
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const paidCount = bills.filter(b => b.active && b.current_cycle_paid).length;
  const totalCount = bills.filter(b => b.active).length;

  // Stale subscription count for badge
  const staleCount = bills.filter(b => b.active && b.is_subscription && getStaleStatus(b).stale).length;

  // Subscription analytics
  const activeSubs = bills.filter(b => b.active && b.is_subscription);
  const subMonthlyTotal = activeSubs.reduce((s, b) => s + (b.amount || 0), 0);
  const subAnnualTotal = subMonthlyTotal * 12;
  const subBuckets = { need: 0, want: 0, savings: 0 };
  const subCategories = {};
  for (const sub of activeSubs) {
    const bucket = sub.bucket || 'want';
    subBuckets[bucket] = (subBuckets[bucket] || 0) + (sub.amount || 0);
    const cat = sub.category || 'Uncategorized';
    subCategories[cat] = (subCategories[cat] || 0) + (sub.amount || 0);
  }
  const subCategoriesSorted = Object.entries(subCategories).sort((a, b) => b[1] - a[1]);

  /**
   * Toggle a bill's paid status.
   */
  const handleTogglePaid = async (bill) => {
    try {
      await toggleBillPaid(user.id, bill.id, !bill.current_cycle_paid, bill.amount);
      await loadBills();
    } catch (err) {
      showMessage('Failed to update: ' + err.message, 'error');
    }
  };

  /**
   * Open add form.
   */
  const handleAdd = () => {
    setFormData({
      name: '', amount: '', due_day: '', category: '',
      bucket: 'need', frequency: 'monthly', auto_pay: false,
      is_subscription: false, variable: false, match_pattern: '',
    });
    setEditingBill(null);
    setMode('add');
  };

  /**
   * Open edit form for a specific bill.
   */
  const handleEdit = (bill) => {
    setFormData({
      name: bill.name || '',
      amount: bill.amount || '',
      due_day: bill.due_day || '',
      bucket: bill.bucket || 'need',
      frequency: bill.frequency || 'monthly',
      auto_pay: bill.auto_pay || false,
      is_subscription: bill.is_subscription || false,
      variable: bill.variable || false,
      match_pattern: bill.match_pattern || '',
    });
    setEditingBill(bill);
    setMode('edit');
  };

  /**
   * Save a new or edited bill.
   */
  const handleSave = async () => {
    if (!formData.name || !formData.amount || !formData.due_day) {
      showMessage('Name, amount, and due day are required.', 'error');
      return;
    }

    const billData = {
      ...formData,
      amount: Number(formData.amount),
      due_day: Number(formData.due_day),
    };

    // Auto-generate match_pattern from bill name if not set
    if (!billData.match_pattern) {
      billData.match_pattern = billData.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    try {
      if (mode === 'edit' && editingBill) {
        await updateBill(user.id, editingBill.id, billData);
        showMessage('Bill updated!', 'success');
      } else {
        await addBill(user.id, billData);
        showMessage('Bill added!', 'success');
      }
      setMode('list');
      await loadBills();
    } catch (err) {
      showMessage('Failed to save: ' + err.message, 'error');
    }
  };

  /**
   * Delete a bill (with confirmation).
   */
  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete "${bill.name}"? This cannot be undone.`)) return;
    try {
      await deleteBill(user.id, bill.id);
      showMessage('Bill deleted.', 'success');
      setMode('list');
      await loadBills();
    } catch (err) {
      showMessage('Failed to delete: ' + err.message, 'error');
    }
  };

  /**
   * Accept a subscription suggestion — creates a bill.
   */
  const handleAccept = async (suggestion) => {
    try {
      await acceptSuggestion(user.id, suggestion.id);
      showMessage(`Added "${suggestion.merchant}" as a bill!`, 'success');
      await loadBills();
    } catch (err) {
      showMessage('Failed to accept: ' + err.message, 'error');
    }
  };

  /**
   * Accept all remaining (non-dismissed) subscription suggestions at once.
   */
  const handleAcceptAll = async () => {
    if (suggestions.length === 0) return;
    try {
      await Promise.all(suggestions.map(sug => acceptSuggestion(user.id, sug.id)));
      showMessage(`Added ${suggestions.length} subscription${suggestions.length > 1 ? 's' : ''} to your bills!`, 'success');
      await loadBills();
    } catch (err) {
      showMessage('Failed to accept all: ' + err.message, 'error');
    }
  };

  /**
   * Dismiss a subscription suggestion.
   */
  const handleDismiss = async (suggestion) => {
    try {
      await dismissSuggestion(user.id, suggestion.id);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (err) {
      showMessage('Failed to dismiss: ' + err.message, 'error');
    }
  };

  /**
   * Mark a stale subscription as still active (resets last_matched_month).
   */
  const handleStillActive = async (bill) => {
    try {
      await updateBill(user.id, bill.id, { last_matched_month: getCurrentMonthKey() });
      await loadBills();
    } catch (err) {
      showMessage('Failed to update: ' + err.message, 'error');
    }
  };

  /**
   * Deactivate a stale subscription.
   */
  const handleDeactivate = async (bill) => {
    try {
      await updateBill(user.id, bill.id, { active: false });
      showMessage(`"${bill.name}" deactivated.`, 'success');
      await loadBills();
    } catch (err) {
      showMessage('Failed to deactivate: ' + err.message, 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ==================== RENDER ====================

  if (loading) return (
    <div>
      <SkeletonCard>
        <SkeletonLine className="skeleton-line--tall" />
        <SkeletonLine className="skeleton-line--short" />
      </SkeletonCard>
      <SkeletonCard>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="skeleton-row">
            <SkeletonLine className="skeleton-line--medium" />
            <SkeletonLine className="skeleton-line--short" />
          </div>
        ))}
      </SkeletonCard>
    </div>
  );

  if (error) return (
    <div className="card">
      <div className="alert alert-error">{error}</div>
      <button className="btn btn-primary" onClick={() => { setError(null); setLoading(true); loadBills(); }}>
        Retry
      </button>
    </div>
  );

  // Add/Edit form
  if (mode === 'add' || mode === 'edit') {
    return (
      <div>
        <div className="card">
          <h2>{mode === 'edit' ? 'Edit Bill' : 'Add Bill'}</h2>

          <div className="form-group">
            <label>Name</label>
            <input value={formData.name} onChange={e => updateForm('name', e.target.value)} placeholder="e.g., Netflix" />
          </div>

          <div className="form-group">
            <label>Monthly Amount ($)</label>
            <input type="number" value={formData.amount} onChange={e => updateForm('amount', e.target.value)} min="0" step="0.01" />
          </div>

          <div className="form-group">
            <label>Due Day (1-31)</label>
            <input type="number" value={formData.due_day} onChange={e => updateForm('due_day', e.target.value)} min="1" max="31" />
          </div>

          <div className="form-group">
            <label>Budget Bucket</label>
            <select value={formData.bucket} onChange={e => updateForm('bucket', e.target.value)}>
              <option value="need">Need</option>
              <option value="want">Want</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <div className="form-group">
            <label>Frequency</label>
            <select value={formData.frequency} onChange={e => updateForm('frequency', e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="biweekly">Biweekly</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={formData.auto_pay} onChange={e => updateForm('auto_pay', e.target.checked)} />
              Auto-pay
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={formData.is_subscription} onChange={e => updateForm('is_subscription', e.target.checked)} />
              Subscription
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={formData.variable} onChange={e => updateForm('variable', e.target.checked)} />
              Variable amount
            </label>
          </div>

          <div className="btn-row">
            <button className="btn btn-primary" onClick={handleSave}>
              {mode === 'edit' ? 'Update Bill' : 'Add Bill'}
            </button>
            <button className="btn btn-secondary" onClick={() => setMode('list')}>Cancel</button>
            {mode === 'edit' && editingBill && (
              <button className="btn btn-danger" onClick={() => handleDelete(editingBill)}>Delete</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div>
      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      {/* Summary bar */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ marginBottom: '0.25rem' }}>Bills</h2>
            <p style={{ fontSize: '0.75rem', color: theme.textMuted }}>
              {paidCount}/{totalCount} paid this cycle
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{formatCurrency(unpaidTotal)}</div>
            <div style={{ fontSize: '0.7rem', color: theme.textMuted }}>remaining</div>
          </div>
        </div>

        {/* Monthly total */}
        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: `1px solid ${theme.borderLight}`, fontSize: '0.8rem', color: theme.textSecondary }}>
          Monthly total: {formatCurrency(monthlyTotal)}
        </div>
      </div>

      {/* Filter tabs + add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <div className="btn-row">
          {['all', 'unpaid', 'subscriptions'].map(f => (
            <button
              key={f}
              className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f)}
              style={{ position: 'relative' }}
            >
              {f === 'all' ? 'All' : f === 'unpaid' ? 'Unpaid' : 'Subs'}
              {f === 'subscriptions' && staleCount > 0 && (
                <span className="stale-badge">{staleCount}</span>
              )}
            </button>
          ))}
        </div>
        <button className="btn btn-success btn-sm" onClick={handleAdd}>+ Add</button>
      </div>

      {/* Subscription Analytics (shown when Subs filter active) */}
      {filter === 'subscriptions' && activeSubs.length > 0 && (
        <SubscriptionAnalytics
          total={subMonthlyTotal}
          annual={subAnnualTotal}
          count={activeSubs.length}
          buckets={subBuckets}
          categories={subCategoriesSorted}
        />
      )}

      {/* Subscription suggestions card */}
      {suggestions.length > 0 && (filter === 'all' || filter === 'subscriptions') && (
        <div className="card sub-suggestions">
          <div className="sub-suggestions-header">
            <h3>New Subscriptions Detected</h3>
            <button className="btn btn-success btn-sm" onClick={handleAcceptAll}>
              Accept All ({suggestions.length})
            </button>
          </div>
          {suggestions.map(sug => (
            <div key={sug.id} className="sub-sug-row">
              <div className="sub-sug-info">
                <div className="sub-sug-name">{sug.merchant}</div>
                <div className="sub-sug-meta">
                  {formatCurrency(sug.amount)}/mo · {sug.occurrence_count} charges
                  {sug.category ? ` · ${sug.category}` : ''}
                </div>
              </div>
              <div className="sub-sug-actions">
                <button className="btn btn-success btn-sm" onClick={() => handleAccept(sug)}>+ Add</button>
                <button className="btn btn-secondary btn-sm" onClick={() => handleDismiss(sug)}>&times;</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bill list */}
      <div className="card">
        {sortedBills.length === 0 ? (
          <p className="empty-state">
            {filter === 'all' ? 'No bills yet. Add bills or seed from baseline in Settings.' : 'No matching bills.'}
          </p>
        ) : (
          sortedBills.map(bill => (
            <BillRow
              key={bill.id}
              bill={bill}
              onTogglePaid={() => handleTogglePaid(bill)}
              onEdit={() => handleEdit(bill)}
              onDelete={() => handleDelete(bill)}
              onStillActive={() => handleStillActive(bill)}
              onDeactivate={() => handleDeactivate(bill)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * BillRow — Individual bill row with paid toggle, edit access, and swipe-to-delete.
 *
 * Swipe left to reveal a red trash button. Tapping it triggers onDelete with confirmation.
 * Tapping elsewhere or swiping back closes the action. Threshold: 60px.
 */
function BillRow({ bill, onTogglePaid, onEdit, onDelete, onStillActive, onDeactivate }) {
  const urgency = !bill.current_cycle_paid ? getBillUrgency(bill.due_day) : 'normal';
  const { stale, lastMonth } = getStaleStatus(bill);
  const contentRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isOpen = useRef(false);
  const isSwiping = useRef(false);

  const ACTIONS_WIDTH = 144; // 72px paid + 72px delete
  const THRESHOLD = 60;

  const setTranslate = (x) => {
    if (contentRef.current) {
      contentRef.current.style.transform = `translateX(${x}px)`;
    }
  };

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    isSwiping.current = false;
    if (contentRef.current) contentRef.current.classList.add('swiping');
  };

  const handleTouchMove = (e) => {
    const deltaX = e.touches[0].clientX - startX.current;
    const base = isOpen.current ? -ACTIONS_WIDTH : 0;
    const newX = Math.min(0, Math.max(-ACTIONS_WIDTH, base + deltaX));
    currentX.current = newX;
    if (Math.abs(deltaX) > 10) isSwiping.current = true;
    setTranslate(newX);
  };

  const handleTouchEnd = () => {
    if (contentRef.current) contentRef.current.classList.remove('swiping');
    if (currentX.current < -THRESHOLD) {
      setTranslate(-ACTIONS_WIDTH);
      isOpen.current = true;
    } else {
      setTranslate(0);
      isOpen.current = false;
    }
  };

  const handleContentClick = () => {
    if (isOpen.current) {
      setTranslate(0);
      isOpen.current = false;
    }
  };

  const handlePaid = () => {
    setTranslate(0);
    isOpen.current = false;
    onTogglePaid();
  };

  const handleDelete = () => {
    setTranslate(0);
    isOpen.current = false;
    onDelete(bill);
  };

  return (
    <div className="swipe-container">
      <div style={{ display: 'flex', position: 'absolute', right: 0, top: 0, bottom: 0, width: `${ACTIONS_WIDTH}px` }}>
        <button
          onClick={handlePaid}
          style={{
            flex: 1, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
            background: bill.current_cycle_paid ? theme.textMuted : theme.success,
            color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>{bill.current_cycle_paid ? '↩' : '✓'}</span>
          {bill.current_cycle_paid ? 'Unpaid' : 'Paid'}
        </button>
        <button
          onClick={handleDelete}
          style={{
            flex: 1, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
            background: theme.danger, color: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>🗑</span>
          Delete
        </button>
      </div>
      <div
        ref={contentRef}
        className="swipe-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContentClick}
      >
        <div className={`bill-row ${bill.bucket || 'need'}${bill.current_cycle_paid ? ' bill-row-paid' : ''}`}>
          {/* Bill info — tap to edit */}
          <div className="bill-info" onClick={(e) => { if (!isSwiping.current && !isOpen.current) { onEdit(); } else { e.stopPropagation(); }}} style={{ cursor: 'pointer' }}>
            <div className={`bill-name ${bill.current_cycle_paid ? 'bill-paid' : ''}`}>
              {bill.is_subscription && '🔄 '}{bill.name}
            </div>
            <div className={`bill-due ${urgency}`}>
              Due {ordinalDay(bill.due_day)}
              {bill.auto_pay && ' · Auto'}
              {bill.variable && ' · ~'}
            </div>
            {stale && (
              <div className="bill-stale-alert">
                No charge since {lastMonth || 'unknown'}
                <span className="bill-stale-actions">
                  <button className="btn-link" onClick={(e) => { e.stopPropagation(); onStillActive(); }}>Still active</button>
                  <button className="btn-link btn-link-danger" onClick={(e) => { e.stopPropagation(); onDeactivate(); }}>Deactivate</button>
                </span>
              </div>
            )}
          </div>

          {/* Amount + desktop actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <div className={`bill-amount ${bill.current_cycle_paid ? 'bill-paid' : ''}`}>
              {formatCurrency(bill.amount)}
            </div>
            {/* Desktop-only action buttons (hidden on mobile via CSS) */}
            <div className="bill-desktop-actions">
              <button
                onClick={(e) => { e.stopPropagation(); onTogglePaid(); }}
                title={bill.current_cycle_paid ? 'Mark unpaid' : 'Mark paid'}
                style={{
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  padding: '0.3rem 0.5rem', fontSize: '0.75rem', fontWeight: 600,
                  background: bill.current_cycle_paid ? theme.border : theme.success + '22',
                  color: bill.current_cycle_paid ? theme.textMuted : theme.success,
                }}
              >
                {bill.current_cycle_paid ? '↩ Unpaid' : '✓ Paid'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(bill); }}
                title="Delete"
                style={{
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                  background: theme.danger + '15', color: theme.danger,
                }}
              >
                🗑
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SubscriptionAnalytics — Inline analytics shown when Subs filter is active.
 * Shows total monthly/annual cost, bucket split bar, and category breakdown.
 */
function SubscriptionAnalytics({ total, annual, count, buckets, categories }) {
  const BUCKET_COLORS = { need: theme.bucketNeed, want: theme.bucketWant, savings: theme.bucketSavings };
  const BUCKET_LABELS = { need: 'Needs', want: 'Wants', savings: 'Savings' };

  return (
    <div className="card">
      {/* Hero stats */}
      <div className="sub-analytics-hero">
        <div className="sub-analytics-total">{formatCurrency(total)}</div>
        <div className="sub-analytics-label">per month · {formatCurrency(annual)}/yr</div>
        <div className="sub-analytics-count">{count} active subscription{count !== 1 ? 's' : ''}</div>
      </div>

      {/* Stacked bar */}
      {total > 0 && (
        <div className="sub-analytics-bar">
          {['need', 'want', 'savings'].map(b => {
            const pct = (buckets[b] / total) * 100;
            if (pct === 0) return null;
            return (
              <div
                key={b}
                className="sub-analytics-bar-segment"
                style={{ width: `${pct}%`, background: BUCKET_COLORS[b] }}
              />
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="sub-analytics-legend">
        {['need', 'want', 'savings'].map(b => (
          buckets[b] > 0 && (
            <div key={b} className="sub-analytics-legend-item">
              <span className="sub-analytics-legend-dot" style={{ background: BUCKET_COLORS[b] }} />
              {BUCKET_LABELS[b]} {formatCurrency(buckets[b])}
            </div>
          )
        ))}
      </div>

      {/* Category breakdown */}
      {categories.length > 0 && (
        <div style={{ marginTop: '0.75rem', borderTop: `1px solid ${theme.borderLight}`, paddingTop: '0.5rem' }}>
          {categories.map(([cat, amt]) => (
            <div key={cat} className="sub-cat-row">
              <span>{cat}</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(amt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
